import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Divider,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import MergeTypeIcon from "@material-ui/icons/MergeType";
import WarningIcon from "@material-ui/icons/Warning";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 400,
    minHeight: 300,
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  contactList: {
    maxHeight: 250,
    overflow: "auto",
  },
  selectedContact: {
    backgroundColor: theme.palette.action.selected,
    border: `2px solid ${theme.palette.primary.main}`,
  },
  currentContactBox: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  warningBox: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
  },
  warningIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.warning.dark,
  },
  mergeInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
  },
  noResults: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
}));

const MergeContactModal = ({ open, onClose, contact, onMergeSuccess }) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  // Buscar contatos
  const searchContacts = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setContacts([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get("/contacts", {
        params: {
          searchParam: term,
          pageNumber: 1,
          // Busca só no cadastro do contato, sem varrer o corpo das mensagens
          // (que traria qualquer contato que apenas citou a palavra).
          searchScope: "contact",
        },
      });
      // Filtrar para não mostrar o contato atual
      const filtered = data.contacts.filter((c) => c.id !== contact?.id);
      setContacts(filtered);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [contact?.id]);

  // Debounce na busca
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchContacts(searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, searchContacts]);

  // Limpar ao fechar
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setContacts([]);
      setSelectedContact(null);
    }
  }, [open]);

  // Realizar merge
  const handleMerge = async () => {
    if (!selectedContact) {
      toast.warning(i18n.t("mergeContactModal.selectContact"));
      return;
    }

    setMerging(true);
    try {
      // O contato atual (que provavelmente tem LID) será o secundário
      // O contato selecionado (com número real) será o principal
      const primaryContactId = selectedContact.number.startsWith("lid_") 
        ? contact.id 
        : selectedContact.id;
      const secondaryContactId = selectedContact.number.startsWith("lid_") 
        ? selectedContact.id 
        : contact.id;

      await api.post("/contacts/merge", {
        primaryContactId,
        secondaryContactId,
      });

      toast.success(i18n.t("mergeContactModal.success"));
      onMergeSuccess && onMergeSuccess();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setMerging(false);
    }
  };

  const formatContactDisplay = (c) => {
    const isLid = c.number?.startsWith("lid_");
    return {
      primary: c.name || c.number,
      secondary: isLid ? `LID: ${c.number.replace("lid_", "")}` : c.number,
      isLid,
    };
  };

  const currentDisplay = contact ? formatContactDisplay(contact) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <MergeTypeIcon style={{ marginRight: 8 }} />
          {i18n.t("mergeContactModal.title")}
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Contato Atual */}
        {contact && (
          <Paper className={classes.currentContactBox} variant="outlined">
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              {i18n.t("mergeContactModal.currentContact")}
            </Typography>
            <Box display="flex" alignItems="center">
              <Avatar src={contact.profilePicUrl?.includes?.('nopicture') ? undefined : contact.profilePicUrl} style={{ marginRight: 12 }}>
                {contact.name?.[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body1">{currentDisplay?.primary}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {currentDisplay?.secondary}
                  {currentDisplay?.isLid && (
                    <Typography
                      component="span"
                      variant="caption"
                      style={{ marginLeft: 8, color: "#ff9800" }}
                    >
                      (LID)
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        <Divider style={{ marginBottom: 16 }} />

        {/* Campo de busca */}
        <TextField
          className={classes.searchField}
          fullWidth
          variant="outlined"
          size="small"
          placeholder={i18n.t("mergeContactModal.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Lista de contatos */}
        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress size={30} />
          </Box>
        ) : contacts.length > 0 ? (
          <List className={classes.contactList}>
            {contacts.map((c) => {
              const display = formatContactDisplay(c);
              return (
                <ListItem
                  key={c.id}
                  button
                  onClick={() => setSelectedContact(c)}
                  className={selectedContact?.id === c.id ? classes.selectedContact : ""}
                >
                  <ListItemAvatar>
                    <Avatar src={c.profilePicUrl?.includes?.('nopicture') ? undefined : c.profilePicUrl}>
                      {c.name?.[0]?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={display.primary}
                    secondary={
                      <>
                        {display.secondary}
                        {display.isLid && (
                          <Typography
                            component="span"
                            variant="caption"
                            style={{ marginLeft: 8, color: "#ff9800" }}
                          >
                            (LID)
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        ) : searchTerm.length >= 2 ? (
          <Typography className={classes.noResults}>
            {i18n.t("mergeContactModal.noContactsFound")}
          </Typography>
        ) : (
          <Typography className={classes.noResults}>
            {i18n.t("mergeContactModal.searchHint")}
          </Typography>
        )}

        {/* Aviso */}
        {selectedContact && (
          <>
            <Box className={classes.warningBox}>
              <WarningIcon className={classes.warningIcon} />
              <Typography variant="body2">
                {i18n.t("mergeContactModal.warning")}
              </Typography>
            </Box>
            <Paper className={classes.mergeInfo} variant="outlined">
              <Typography variant="body2">
                <strong>{i18n.t("mergeContactModal.mergeInfoTitle")}</strong>
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
                  <li>{i18n.t("mergeContactModal.mergeInfo1")}</li>
                  <li>{i18n.t("mergeContactModal.mergeInfo2")}</li>
                  <li>{i18n.t("mergeContactModal.mergeInfo3")}</li>
                </ul>
              </Typography>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={merging}>
          {i18n.t("mergeContactModal.buttons.cancel")}
        </Button>
        <Button
          onClick={handleMerge}
          color="primary"
          variant="contained"
          disabled={!selectedContact || merging}
          startIcon={merging ? <CircularProgress size={20} /> : <MergeTypeIcon />}
        >
          {merging
            ? i18n.t("mergeContactModal.buttons.merging")
            : i18n.t("mergeContactModal.buttons.merge")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeContactModal;
